/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable default-case */
import React from 'react';
import { BattleshipGameTypes, CardData } from './types';
import { Answer, CircularCountdown, LinearCountdown, Question } from 'components';
import { Question as QuestionFull } from 'types';

import wavesImg from 'assets/waves3.png';
import explosionImg from 'assets/explosion-1.png';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Backdrop from '@mui/material/Backdrop';

export const Views = {
    location: 1,
    question: 2,
    answer: 3,
    splashImage: 4,
    singlePlayerQuestion: 5
};

interface Props {
    cardData: CardData;
    column: number;
    row: number;
    left: number;
    top: number;
    height: number;
    width: number;
    key: React.Key;
    eventLogo?: string | null;
    gameType: BattleshipGameTypes;
    maxQuestionDuration?: number | null;
    incorrectAnswerCooldownSeconds?: number | null;
    onIncorrectAnswer: (cardData: CardData, userAnswer?: string) => void;
    onCorrectAnswer: (cardData: CardData, userAnswer?: string) => void;
}

interface State {
    view: number;
    flipping: boolean;
    correctAnswer: string;
    cooldownActive: boolean;
    cooldownSize: number;
}

class Card extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            view: Views.location,
            flipping: false,
            cooldownActive: false,
            correctAnswer: props.cardData.question!.answers.find((a) => a.isCorrect)!.answer!,
            cooldownSize: (props.height > props.width ? props.width : props.height) * 0.8
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        const changedState: Partial<State> = {
            cooldownSize: (props.height > props.width ? props.width : props.height) * 0.8
        };
        if (props.cardData.answered) {
            changedState.view = Views.location;
        }
        return changedState;
    }

    onCardClickHandler() {
        switch (this.state.view) {
            case Views.location:
                if (this.props.gameType !== BattleshipGameTypes.LIVE_MULTI_TEAM) {
                    this.setState({ view: Views.singlePlayerQuestion, flipping: true });
                    return;
                }
                this.setState({ view: Views.question, flipping: true });
                return;

            case Views.question:
                this.setState({ view: Views.answer });
                return;

            case Views.splashImage:
                this.setState({ view: this.props.gameType !== BattleshipGameTypes.LIVE_MULTI_TEAM ? Views.singlePlayerQuestion : Views.question });
                return;

            default:
                // This is a fallback
                this.setState({ view: Views.location });
                return;
        }
    }

    handleCorrectAnswer() {
        const correctAnswer = this.props.cardData.question?.answers.find(a => a.isCorrect);
        this.handleAnsweredQuestion(true, undefined, correctAnswer?.answer);
    }

    handleIncorrectAnswer() {
        this.handleAnsweredQuestion(false, undefined, '');
    }

    handleAnsweredQuestion(isAnswerCorrect: boolean, _?: QuestionFull, userAnswer?: string) {
        if (isAnswerCorrect) {
            this.props.onCorrectAnswer(this.props.cardData, userAnswer);
        } else {
            this.setState({ cooldownActive: true });
            this.props.onIncorrectAnswer(this.props.cardData, userAnswer);
        }
        this.setState({ view: Views.location, flipping: true });
    }

    transitionEndHandler(event: { propertyName: string }) {
        if (event.propertyName === 'width') {
            this.setState({ flipping: false });
        }
    }

    clearCooldown() {
        this.setState({ cooldownActive: false });
    }

    renderFront() {
        return !this.props.cardData.answered ? (
            <>
                <span className="points">{this.props.cardData.locationCode}</span>
                {this.props.incorrectAnswerCooldownSeconds && this.state.cooldownActive && (

                    <Backdrop
                        sx={{ backgroundColor: 'rgb(50 50 50 / 50%)', zIndex: (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer + 1 }}
                        open={true}
                    >
                        <CircularCountdown size={this.state.cooldownSize} onTimeout={this.clearCooldown.bind(this)} seconds={this.props.incorrectAnswerCooldownSeconds} />
                    </Backdrop>
                )}
            </>
        ) : this.props.cardData.shipTeam ? (
            <>
                <img src={explosionImg} alt="shipwreck-image" />
                {this.props.cardData.shipTeam}
            </>
        ) : (
            <img src={wavesImg} alt="waves-image" />
        );
    }

    getLabelBack() {
        switch (this.state.view) {
            case Views.answer:
                return (
                    <Answer question={this.props.cardData.question!}>
                        <Stack className="answer-buttons" direction="row" spacing={2}>
                            <Button variant="contained" className="incorrect" onClick={this.handleIncorrectAnswer.bind(this)}>
                                Maybe Next Time
                            </Button>
                            <Button variant="contained" className="correct" onClick={this.handleCorrectAnswer.bind(this)}>
                                Correct
                            </Button>
                        </Stack>
                    </Answer>
                );
            // case Views.splashImage:
            //      // TODO: Implement Splash Image option
            //     const folderWithPrefix = this.props.question.isStarWars ? 'star-wars/SW' : 'star-trek/ST';
            //     return {__html: `<img src="assets/img/${folderWithPrefix}-ships-${this.state.imgIndex}.png"/>`};
            case Views.singlePlayerQuestion:
                return (
                    <>
                        {this.props.maxQuestionDuration && (
                            <LinearCountdown seconds={this.props.maxQuestionDuration} onTimeout={this.handleIncorrectAnswer.bind(this)} />
                        )}
                        <Answer question={this.props.cardData.question!} onAnswer={this.handleAnsweredQuestion.bind(this)} />
                    </>
                );
            case Views.question:
            default:
                return <Question question={this.props.cardData.question!} baseClassName="question" showMultipleChoiceOptions={true} />;
        }
    }

    getOnCardClickHandler() {
        const nonClickableViews = [Views.answer, Views.singlePlayerQuestion];
        if (nonClickableViews.indexOf(this.state.view) > -1 || !!this.props.cardData.answered || this.state.cooldownActive) return;

        return this.onCardClickHandler.bind(this);
    }

    render() {
        let style = {
                width: this.props.width + 'px',
                height: this.props.height + 'px',
                transform: 'translate3d(' + this.props.left + 'px,' + this.props.top + 'px,0)',
                WebkitTransform: 'translate3d(' + this.props.left + 'px,' + this.props.top + 'px,0)'
            },
            className = 'flipper';

        if (this.state.view !== Views.location) {
            className = className + ' flipped';
        }

        if (this.state.flipping) {
            className = className + ' flipping';
        }

        return (
            <div
                style={style}
                className={className}
                onClick={this.getOnCardClickHandler()}
                onTransitionEnd={this.transitionEndHandler.bind(this)}
            >
                <div className="card">
                    <div className="front">{this.renderFront()}</div>
                    <div className="back">
                        {this.state.view !== Views.location && this.getLabelBack()}
                        {!!this.props.eventLogo && <img className="header-logo" src={this.props.eventLogo} alt="event-logo" />}
                    </div>
                </div>
            </div>
        );
    }
}

export default Card;
